import type { StatusTone } from "@/components/ui";
import type {
  AppealAction,
  AppealStatus,
  EnforcementActionType,
  EnforcementLifecycleAction,
  EnforcementStatus,
  ModerationCaseAction,
  ModerationCaseStatus,
  ModerationSeverity,
  ReportAction,
  ReportStatus,
} from "./types";

export function formatLabel(value: string): string {
  return value.replaceAll("_", " ");
}

export function reportStatusTone(status: ReportStatus): StatusTone {
  switch (status) {
    case "submitted":
      return "warning";
    case "triaged":
    case "linked_or_opened":
      return "info";
    case "resolved":
      return "success";
    case "closed":
      return "neutral";
    default:
      return "neutral";
  }
}

export function caseStatusTone(status: ModerationCaseStatus): StatusTone {
  switch (status) {
    case "new":
    case "triage":
      return "warning";
    case "investigating":
    case "awaiting_information":
      return "info";
    case "actioned":
      return "success";
    case "appealed":
      return "warning";
    case "closed":
      return "neutral";
    default:
      return "neutral";
  }
}

export function severityTone(severity: ModerationSeverity): StatusTone {
  switch (severity) {
    case "low":
      return "info";
    case "medium":
      return "warning";
    case "high":
    case "critical":
      return "danger";
    default:
      return "neutral";
  }
}

export function enforcementStatusTone(status: EnforcementStatus): StatusTone {
  switch (status) {
    case "proposed":
      return "info";
    case "approved":
      return "warning";
    case "active":
      return "danger";
    case "expired":
    case "lifted":
    case "reversed":
    case "superseded":
      return "neutral";
    default:
      return "neutral";
  }
}

export function appealStatusTone(status: AppealStatus): StatusTone {
  switch (status) {
    case "submitted":
    case "more_information_required":
      return "warning";
    case "under_review":
      return "info";
    case "upheld":
    case "modified":
    case "reversed":
      return "success";
    case "withdrawn":
    case "closed":
      return "neutral";
    default:
      return "neutral";
  }
}

export const REPORT_ACTIONS_FROM: Record<ReportAction, readonly ReportStatus[]> =
  {
    triage: ["submitted"],
    link_or_open: ["submitted", "triaged"],
    resolve: ["triaged", "linked_or_opened"],
    close: ["submitted", "triaged", "linked_or_opened", "resolved"],
  };

export const CASE_ACTIONS_FROM: Record<
  ModerationCaseAction,
  readonly ModerationCaseStatus[]
> = {
  triage: ["new"],
  investigate: ["new", "triage", "awaiting_information"],
  await_info: ["triage", "investigating"],
  action: ["triage", "investigating", "awaiting_information"],
  mark_appealed: ["actioned"],
  close: [
    "new",
    "triage",
    "investigating",
    "awaiting_information",
    "actioned",
    "appealed",
  ],
  assign: [
    "new",
    "triage",
    "investigating",
    "awaiting_information",
    "actioned",
    "appealed",
  ],
};

export const ENFORCEMENT_ACTIONS_FROM: Record<
  EnforcementLifecycleAction,
  readonly EnforcementStatus[]
> = {
  approve: ["proposed"],
  activate: ["approved"],
  expire: ["active"],
  lift: ["active"],
  reverse: ["proposed", "approved", "active"],
  supersede: ["proposed", "approved", "active"],
};

export const APPEAL_ACTIONS_FROM: Record<AppealAction, readonly AppealStatus[]> =
  {
    start_review: ["submitted", "more_information_required"],
    request_info: ["under_review"],
    decide_upheld: ["under_review", "more_information_required"],
    decide_modified: ["under_review", "more_information_required"],
    decide_reversed: ["under_review", "more_information_required"],
    withdraw: ["submitted", "under_review", "more_information_required"],
    close: [
      "upheld",
      "modified",
      "reversed",
      "withdrawn",
      "under_review",
      "more_information_required",
    ],
  };

export function allowedReportActions(status: ReportStatus): ReportAction[] {
  return (Object.keys(REPORT_ACTIONS_FROM) as ReportAction[]).filter((action) =>
    REPORT_ACTIONS_FROM[action].includes(status),
  );
}

export function allowedCaseActions(
  status: ModerationCaseStatus,
): ModerationCaseAction[] {
  return (Object.keys(CASE_ACTIONS_FROM) as ModerationCaseAction[]).filter(
    (action) => CASE_ACTIONS_FROM[action].includes(status),
  );
}

export function allowedEnforcementActions(
  status: EnforcementStatus,
): EnforcementLifecycleAction[] {
  return (
    Object.keys(ENFORCEMENT_ACTIONS_FROM) as EnforcementLifecycleAction[]
  ).filter((action) => ENFORCEMENT_ACTIONS_FROM[action].includes(status));
}

export function allowedAppealActions(status: AppealStatus): AppealAction[] {
  return (Object.keys(APPEAL_ACTIONS_FROM) as AppealAction[]).filter((action) =>
    APPEAL_ACTIONS_FROM[action].includes(status),
  );
}

export function enforcementNeedsDual(
  actionType: EnforcementActionType,
  highImpact: boolean,
): boolean {
  return actionType === "permanent_ban" || highImpact;
}

/** Convert datetime-local (or any parseable date) to UTC ISO with offset. */
export function toIsoUtc(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) return trimmed;
  return date.toISOString();
}
